import React, { useState } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  Collapse,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import { useDrag, useDrop } from 'react-dnd'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import _cloneDeep from 'lodash.clonedeep'
import IndicatorParameterDialog from '../condition/indicatorParameterDialogInputs/IndicatorParameterDialog'
import Indicator from '../condition/indicators/types/Indicator'
import standardIndicators from '../condition/indicators/standard'
import { CompositeValidationIssue, NODE_CATALOG } from '../../../common/utils/composite'
import ExprEditor from './ExprEditor'
import StepRefSelect from './StepRefSelect'
import { INVALID_BORDER_COLOR, INVALID_GLOW, INVALID_TRANSITION } from './invalidStyles'
import { DraftStep, stepSummary } from './types'

const KIND_LABELS: Record<DraftStep['kind'], string> = {
  indicator: 'Indicator',
  math: 'Math',
}

const STEP_DND_TYPE = 'indicator-step'

const chainableIndicators = standardIndicators.filter((i) => NODE_CATALOG[i.value])

interface StepCardProps {
  step: DraftStep
  index: number
  steps: DraftStep[]
  onChange: (step: DraftStep) => void
  onDelete: () => void
  onMove: (from: number, to: number) => void
  deleteDisabledReason?: string
  getIndicatorByValue: (value: string) => Indicator | undefined
  issues?: CompositeValidationIssue[]
  defaultExpanded?: boolean
}

/**
 * A step rendered with the pattern builder's condition anatomy: a compact
 * header (drag handle · number · "Kind: details" summary · hover-reveal
 * delete) that toggles a collapsible editor body, and drag-and-drop
 * reordering between steps. A reorder that breaks a reference is allowed —
 * validation flags the offending card with the usual red glow.
 */
const StepCard: React.FC<StepCardProps> = ({
  step,
  index,
  steps,
  onChange,
  onDelete,
  onMove,
  deleteDisabledReason,
  getIndicatorByValue,
  issues = [],
  defaultExpanded = false,
}) => {
  const theme = useTheme()
  const [expanded, setExpanded] = useState(defaultExpanded)
  const catalog = step.indicatorName ? NODE_CATALOG[step.indicatorName] : undefined
  const hasError = issues.some((issue) => issue.level === 'error')
  const visibleIssues = issues.filter((issue) => !issue.silent)

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: STEP_DND_TYPE,
      item: { index },
      collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    }),
    [index]
  )

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: STEP_DND_TYPE,
      canDrop: (item: { index: number }) => item.index !== index,
      drop: (item: { index: number }) => onMove(item.index, index),
      collect: (monitor) => ({ isOver: !!monitor.isOver(), canDrop: !!monitor.canDrop() }),
    }),
    [index, onMove]
  )

  // The series input supersedes the indicator's own price-field param, so hide
  // `field` from the params dialog while an input ref is set.
  const visibleParams = React.useMemo(() => {
    if (!step.params) return undefined
    if (!step.input) return step.params
    const { field, ...rest } = step.params
    return Object.keys(rest).length > 0 ? rest : undefined
  }, [step.params, step.input])

  const dropTarget = isOver && canDrop

  return (
    <Box
      ref={drop}
      sx={{
        backgroundColor: 'background.custom2',
        border: '1px solid',
        // Drop-target highlight outranks invalid red (transient interaction
        // feedback, same precedence as the pattern editor's)
        borderColor: dropTarget
          ? 'primary.main'
          : hasError
            ? INVALID_BORDER_COLOR
            : 'rgba(255, 255, 255, 0.06)',
        boxShadow: dropTarget
          ? `0px 0px 8px ${alpha(theme.palette.primary.main, 0.4)}`
          : hasError
            ? INVALID_GLOW
            : 'none',
        transition: INVALID_TRANSITION,
        borderRadius: '6px',
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        flexDirection: 'column',
        '&:hover .step-delete': { opacity: 1 },
      }}
    >
      {/* Header — mirrors ConditionHeader: drag ref on the whole row, click
          toggles the editor body */}
      <Box
        ref={drag}
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          // width:100% + padding overflows the card without this (ConditionHeader
          // sets it too — CssBaseline's border-box isn't global here)
          boxSizing: 'border-box',
          px: 1.5,
          py: 1,
          gap: 1,
          cursor: 'pointer',
          borderRadius: '6px',
          transition: 'background-color 0.15s ease',
          '&:hover': { backgroundColor: 'background.custom3' },
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            color: 'rgba(255, 255, 255, 0.25)',
            cursor: 'grab',
            transition: 'color 0.15s ease',
            '&:hover': { color: 'rgba(255, 255, 255, 0.6)' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Chip
          size="small"
          label={index + 1}
          sx={{
            height: 22,
            minWidth: 28,
            fontWeight: 700,
            fontSize: '0.7rem',
            backgroundColor: 'background.custom3',
            border: '1px solid',
            borderColor: 'background.custom4',
          }}
        />
        <Typography variant="body2" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
          {KIND_LABELS[step.kind]}
          <Typography component="span" variant="body2" color="text.secondary">
            : {stepSummary(step, steps, getIndicatorByValue)}
          </Typography>
        </Typography>
        <Tooltip title={deleteDisabledReason ?? 'Remove step'}>
          <span>
            <IconButton
              className="step-delete"
              size="small"
              disabled={!!deleteDisabledReason}
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              sx={{
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: `${theme.palette.error.main}20`,
                  color: 'error.main',
                },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit sx={{ width: '100%' }}>
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
          }}
        >
          {step.kind === 'indicator' && (
            <Box display="flex" flexDirection="column" gap={1}>
              <Autocomplete
                size="small"
                value={
                  step.indicatorName ? (getIndicatorByValue(step.indicatorName) ?? null) : null
                }
                onChange={(_, newValue) => {
                  if (newValue && newValue.value) {
                    onChange({
                      ...step,
                      indicatorName: newValue.value,
                      params: _cloneDeep(newValue.params) ?? {},
                      input: undefined,
                    })
                  }
                }}
                options={chainableIndicators}
                getOptionLabel={(option) =>
                  option.label.length > 13 && option.nickname ? option.nickname : option.label
                }
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => <TextField {...params} label="Indicator" />}
                sx={{ minWidth: 200 }}
              />

              {catalog?.acceptsSeriesInput && (
                <StepRefSelect
                  steps={steps}
                  beforeIndex={index}
                  value={step.input}
                  onChange={(input) => onChange({ ...step, input })}
                  getIndicatorByValue={getIndicatorByValue}
                  allowClear
                  clearLabel="Default (indicator's own price field)"
                  label="Input"
                />
              )}

              {visibleParams && (
                <IndicatorParameterDialog
                  onIndicatorParameterChange={(key: string, value: any) =>
                    onChange({ ...step, params: { ...step.params, [key]: value } })
                  }
                  parameters={visibleParams}
                  indicatorName={
                    step.indicatorName ? getIndicatorByValue(step.indicatorName)?.label : undefined
                  }
                  viewOnly={false}
                />
              )}
            </Box>
          )}

          {step.kind === 'math' && (
            <ExprEditor
              expr={step.expr ?? { t: 'const', value: 0 }}
              onChange={(expr) => onChange({ ...step, expr })}
              steps={steps}
              beforeIndex={index}
              getIndicatorByValue={getIndicatorByValue}
            />
          )}

          {visibleIssues.length > 0 && (
            <Box display="flex" flexDirection="column" gap={0.25}>
              {visibleIssues.map((issue, issueIndex) => (
                <Typography
                  key={issueIndex}
                  variant="caption"
                  sx={{ color: issue.level === 'error' ? INVALID_BORDER_COLOR : 'warning.main' }}
                >
                  {issue.message}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export default StepCard
